import { useState } from 'react';
import { Card } from './card';
import { Badge } from './badge';
import { CustomTooltip } from './tooltip';
import { WarningCircle, Lightning, CheckCircle, Lock } from '@phosphor-icons/react';
import type { AlgorithmInfo, AlgorithmRecommendation } from '../../services/types';

interface DeconvolutionAlgorithmSelectorProps {
  selectedAlgorithms: string[];
  onSelectionChange: (algorithms: string[]) => void;
  availableAlgorithms?: AlgorithmInfo[];
  recommendations?: AlgorithmRecommendation[];
  loading?: boolean;
  disabled?: boolean;
  maxSelections?: number;
}

export function DeconvolutionAlgorithmSelector({
  selectedAlgorithms,
  onSelectionChange,
  availableAlgorithms = [],
  recommendations = [],
  loading = false,
  disabled = false,
  maxSelections = 3,
}: DeconvolutionAlgorithmSelectorProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleAlgorithm = (algorithmName: string) => {
    if (disabled) return;

    const isSelected = selectedAlgorithms.includes(algorithmName);
    
    if (isSelected) {
      onSelectionChange(selectedAlgorithms.filter(a => a !== algorithmName));
    } else if (selectedAlgorithms.length < maxSelections) {
      onSelectionChange([...selectedAlgorithms, algorithmName]);
    }
  };

  const toggleExpanded = (algorithmName: string) => {
    setExpanded(prev => ({
      ...prev,
      [algorithmName]: !prev[algorithmName]
    }));
  };

  const getRecommendationScore = (name: string): number | undefined => {
    const rec = recommendations.find(r => r.algorithm.name === name);
    return rec?.score;
  };

  const getRecommendationReasons = (name: string): string[] => {
    const rec = recommendations.find(r => r.algorithm.name === name);
    return rec?.reasons || [];
  };

  if (loading) {
    return (
      <Card className="p-6 bg-gray-50">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-2"></div>
            <p className="text-muted-foreground">Loading algorithms...</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Deconvolution Algorithms</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Select 1-{maxSelections} algorithms to run. Results will be computed for each.
          </p>
        </div>
        <Badge variant="secondary">{selectedAlgorithms.length}/{maxSelections}</Badge>
      </div>

      {recommendations.length > 0 && (
        <Card className="p-4 bg-teal-50 border border-teal-200">
          <div className="flex items-start gap-3">
            <Lightning className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-teal-900 text-sm">Recommended Algorithms</h4>
              <p className="text-xs text-teal-700 mt-1">
                Based on your data characteristics, these algorithms are recommended for best results:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {recommendations.slice(0, 3).map(rec => (
                  <Badge key={rec.algorithm.name} variant="default" className="bg-teal-600">
                    {rec.algorithm.name} ({(rec.score * 100).toFixed(0)}%)
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {availableAlgorithms.map(algorithm => {
          const isSelected = selectedAlgorithms.includes(algorithm.name);
          const score = getRecommendationScore(algorithm.name);
          const reasons = getRecommendationReasons(algorithm.name);
          const isExpanded = expanded[algorithm.name];

          return (
            <Card
              key={algorithm.name}
              className={`p-4 cursor-pointer transition-all ${
                isSelected
                  ? 'border-2 border-teal-500 bg-teal-50'
                  : disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50'
              }`}
              onClick={() => !disabled && toggleAlgorithm(algorithm.name)}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-teal-500 border-teal-500'
                    : 'border-gray-300 bg-white'
                }`}>
                  {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{algorithm.name}</h4>
                    {algorithm.category === 'r' && (
                      <CustomTooltip content="R-based algorithm">
                        <Badge variant="outline" className="text-xs">R</Badge>
                      </CustomTooltip>
                    )}
                    {algorithm.requires_single_cell_data && (
                      <CustomTooltip content="Requires single-cell reference data">
                        <Lock className="w-4 h-4 text-orange-500" />
                      </CustomTooltip>
                    )}
                    {score !== undefined && (
                      <CustomTooltip content={`Recommendation score: ${(score * 100).toFixed(0)}%`}>
                        <Badge variant="secondary" className="text-xs bg-teal-100 text-teal-800">
                          ⭐ {(score * 100).toFixed(0)}%
                        </Badge>
                      </CustomTooltip>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">{algorithm.description}</p>

                  <div className="flex flex-wrap gap-2 mb-2">
                    <CustomTooltip content={`Minimum genes required: ${algorithm.min_genes}`}>
                      <Badge variant="outline" className="text-xs">
                        Min: {algorithm.min_genes} genes
                      </Badge>
                    </CustomTooltip>
                    {algorithm.bulk_supports?.tpm && (
                      <Badge variant="outline" className="text-xs">✓ TPM</Badge>
                    )}
                    {algorithm.bulk_supports?.counts && (
                      <Badge variant="outline" className="text-xs">✓ Counts</Badge>
                    )}
                    {algorithm.bulk_supports?.log_transformed && (
                      <Badge variant="outline" className="text-xs">✓ Log-transformed</Badge>
                    )}
                  </div>

                  {reasons.length > 0 && (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(algorithm.name);
                        }}
                      >
                        {isExpanded ? '▼' : '▶'} Why recommended? ({reasons.length})
                      </button>
                      {isExpanded && (
                        <ul className="mt-2 space-y-1">
                          {reasons.map((reason, idx) => (
                            <li key={idx} className="text-xs text-muted-foreground flex gap-2">
                              <span className="text-teal-600 flex-shrink-0">•</span>
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {selectedAlgorithms.length === 0 && (
        <Card className="p-4 bg-amber-50 border border-amber-200 flex items-start gap-3">
          <WarningCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">No algorithms selected</p>
            <p className="text-xs text-amber-700 mt-1">Please select at least one algorithm to proceed.</p>
          </div>
        </Card>
      )}

      {selectedAlgorithms.length > 0 && (
        <Card className="p-3 bg-teal-50 border border-teal-200">
          <p className="text-xs text-teal-800 font-medium">
            ✓ {selectedAlgorithms.length} algorithm{selectedAlgorithms.length !== 1 ? 's' : ''} selected
          </p>
          <p className="text-xs text-teal-700 mt-1">
            Results will be computed for: <strong>{selectedAlgorithms.join(', ')}</strong>
          </p>
        </Card>
      )}
    </div>
  );
}
